import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";

import {
  ArrowBack,
  CheckCircle,
  CloudDownload,
  ErrorOutline,
  InstallDesktop,
  Pending,
  Refresh,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { can } from "../../../auth/can";
import { useAuth } from "../../../auth/useAuth";
import DispatchOperationsPanel from "./DispatchOperationsPanel";

import { APP_API_V1_BASE } from "../../../config/api";

const REFRESH_INTERVAL_MS = 5000;
const DASHBOARD_GRADIENT =
 "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))";

const STATUS_META = {
  total_devices: {
    label: "Target Devices",
    color: "#3B82F6",
    icon: <Pending fontSize="small" />,
  },
  pending: {
    label: "Pending Ack",
    color: "#F59E0B",
    icon: <Pending fontSize="small" />,
  },
  downloading: {
    label: "Downloading",
    color: "#2563EB",
    icon: <CloudDownload fontSize="small" />,
  },
  installing: {
    label: "Installing",
    color: "#7C3AED",
    icon: <InstallDesktop fontSize="small" />,
  },
  installed: {
    label: "Installed",
    color: "#16A34A",
    icon: <CheckCircle fontSize="small" />,
  },
  failed: {
    label: "Failed",
    color: "#DC2626",
    icon: <ErrorOutline fontSize="small" />,
  },
};


function formatTimestampAdd530(value) {
  if (!value) return "Pending";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Pending";

  // Add 5 hours 30 minutes
  date.setMinutes(date.getMinutes() + 330);

  return date.toLocaleString();
}

const getProgressPhaseLabel = (data) => {
  const scheduledAt = data?.scheduled_at ? new Date(data.scheduled_at) : null;

  if (
    data?.rollout_strategy === "SCHEDULED" &&
    data?.status === "PENDING" &&
    scheduledAt &&
    !Number.isNaN(scheduledAt.getTime()) &&
    scheduledAt.getTime() > Date.now()
  ) {
    return "Scheduled";
  }

  if (data?.status === "IN_PROGRESS") return "Dispatched";
  if (data?.status === "PENDING") return "Pending Ack";
  if (data?.status === "COMPLETED") return "Completed";
  if (data?.status === "FAILED") return "Failed";
  if (data?.status === "CANCELED") return "Canceled";
  return data?.status || "-";
};

function SummaryCard({ label, value, color, icon }) {
  return (
   <Card
  sx={{
    height: "100%",
    borderRadius: 4,
    border: `1px solid ${color}22`,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.07)",
    p: 2, // 👈 THIS FIXES EVERYTHING
  }}
>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `${color}14`,
            color,
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: 28, fontWeight: 800, color }}>
          {value}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 700 }}>
        {label}
      </Typography>
    </Card>
  );
}

export default function DeploymentProgress() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  const { permissions, activeOrg } = useAuth();
  const isPlatformAdmin = can(permissions, "system.manage");
  const canManageDispatch = can(permissions, "firmware.deploy");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeploying, setRedeploying] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      ...(activeOrg?.id ? { "X-Org-ID": activeOrg.id } : {}),
    }),
    [token, activeOrg?.id],
  );

  const fetchProgress = useCallback(async () => {
    if (!isPlatformAdmin) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${APP_API_V1_BASE}/firmware/deployments/${id}/progress`,
        {
          headers,
        },
      );
      setData(res.data);
      setError("");
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Failed to fetch deployment progress", err);
      setError(
        err?.response?.data?.error ||
          "Unable to load deployment progress right now.",
      );
    } finally {
      setLoading(false);
    }
  }, [headers, id, isPlatformAdmin]);

  const handleRedeploy = useCallback(async () => {
    if (!isPlatformAdmin || !id) return;
    setRedeploying(true);
    setError("");
    try {
      const res = await axios.post(
        `${APP_API_V1_BASE}/firmware/deployments/${id}/redeploy`,
        {},
        {
          headers,
        },
      );
      const newDeploymentId = res?.data?.deployment?.id;
      if (newDeploymentId) {
        navigate(`/ota/deployments/${newDeploymentId}`);
        return;
      }
      fetchProgress();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to redeploy deployment.");
    } finally {
      setRedeploying(false);
    }
  }, [fetchProgress, headers, id, isPlatformAdmin, navigate]);

  useEffect(() => {
    if (!isPlatformAdmin) return undefined;
    fetchProgress();

    const timer = setInterval(fetchProgress, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchProgress, isPlatformAdmin]);

  const summary = data?.summary || {};
  const successRate = summary.success_rate || 0;
  const summaryCards = useMemo(
    () =>
      Object.entries(STATUS_META).map(([key, meta]) => ({
        key,
        label: meta.label,
        value: summary[key] ?? 0,
        color: meta.color,
        icon: meta.icon,
      })),
    [summary],
  );

  if (!isPlatformAdmin) {
    return (
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Firmware deployment progress is available only to platform
          administrators.
        </Alert>
      </Paper>
    );
  }

  return (
    <Box
      sx={{ p: { xs: 1.5, md: 3 }, background: "#F8FAFC", minHeight: "100vh" }}
    >
      <Box
        sx={{
          mb: 3,
          borderRadius: 6,
          overflow: "hidden",
          border: "1px solid rgba(15,23,42,0.08)",
          background: DASHBOARD_GRADIENT,
          boxShadow: "0 30px 80px rgba(15,23,42,0.18)",
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3.25 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", xl: "center" },
              flexDirection: { xs: "column", xl: "row" },
              gap: 2.5,
            }}
          >
            <Box sx={{ maxWidth: 760 }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate("/ota/deployments")}
                sx={{
                  mb: 1.5,
                  px: 0,
                  textTransform: "none",
                  color: "#DBEAFE",
                  fontWeight: 700,
                }}
              >
                Back to deployments
              </Button>
              <Typography
                sx={{
                  color: "#F8FAFC",
                  fontSize: { xs: 28, md: 40 },
                  fontWeight: 600,
                  letterSpacing: "-0.05em",
                  lineHeight: 0.98,
                }}
              >
                Firmware Deployment Progress
              </Typography>
              <Typography
                sx={{
                  mt: 1.4,
                  color: "rgba(226,232,240,0.84)",
                  fontSize: { xs: 14, md: 16 },
                  lineHeight: 1.75,
                  maxWidth: 720,
                }}
              >
                Live OTA rollout state driven by backend acknowledgements from
                devices and simulators across pending, download, install, and
                completion stages.
              </Typography>
            </Box>

            <Box sx={{ width: { xs: "100%", xl: 340 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: "rgba(226,232,240,0.72)",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      Overall success rate
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.75,
                        color: "#F8FAFC",
                        fontWeight: 900,
                        fontSize: 32,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      {successRate}%
                    </Typography>
                  </Box>
                  <Chip
                    label={getProgressPhaseLabel(data)}
                    sx={{
                      fontWeight: 700,
                      color: "#E0F2FE",
                      backgroundColor: "rgba(14,165,233,0.16)",
                      border: "1px solid rgba(125,211,252,0.16)",
                    }}
                  />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={successRate}
                  sx={{
                    mt: 2,
                    height: 10,
                    borderRadius: 999,
                    background: "rgba(226,232,240,0.22)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      background:
                        successRate >= 80
                          ? "#16A34A"
                          : successRate >= 40
                            ? "#2563EB"
                            : "#DC2626",
                    },
                  }}
                />
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
                  onClick={fetchProgress}
                  disabled={loading}
                  sx={{
                    mt: 2,
                    textTransform: "none",
                    borderRadius: 3,
                    fontWeight: 800,
                    color: "#F8FAFC",
                    borderColor: "rgba(255,255,255,0.18)",
                  }}
                >
                  Refresh progress
                </Button>
                {String(data?.status || "").toUpperCase() === "FAILED" && (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleRedeploy}
                    disabled={redeploying}
                    sx={{
                      mt: 1.2,
                      textTransform: "none",
                      borderRadius: 3,
                      fontWeight: 800,
                      color: "#082F49",
                      background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                      boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                    }}
                  >
                    {redeploying ? "Redeploying..." : "Redeploy"}
                  </Button>
                )}
              </Paper>
            </Box>
          </Box>
        </CardContent>
      </Box>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 3 }}>
        The progress cards below are driven by the backend OTA listener that
        consumes your frozen MQTT packets: <strong>Update_Ack</strong>,{" "}
        <strong>Download_Initiated</strong>, <strong>Download_Completed</strong>
        , <strong>Install_Initiated</strong>, <strong>Install_Completed</strong>
        , and <strong>Update_Failed</strong>. Scheduled rollouts stay in{" "}
        <strong>Scheduled</strong> until the backend dispatcher publishes them,
        then move to <strong>Dispatched</strong> and wait for device
        acknowledgements. A deployment that remains in pending ack with no
        device response will automatically time out and move to failed so it can
        be retried safely.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
          {error}
        </Alert>
      )}

      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 4,
          mb: 2.5,
          border: "1px solid rgba(148,163,184,0.14)",
          boxShadow: "0 18px 40px rgba(15,23,42,0.06)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(4, minmax(0, 1fr))",
            },
            width:"100%",
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
             <Typography
    sx={{
      fontSize: 12,
      color: "#94A3B8",
      fontWeight: 700,
      minWidth: { xs: 140, sm: "auto" }, // 👈 aligns labels
    }}
  >
    Deployment ID
  </Typography>
            <Typography sx={{ fontWeight: 700, color: "#0F172A" }}>
              {data?.deployment_id || id}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: 12, color: "#94A3B8", fontWeight: 700 }}
            >
              Firmware ID
            </Typography>
            <Typography sx={{ fontWeight: 700, color: "#0F172A" }}>
              {data?.firmware_id || "-"}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: 12, color: "#94A3B8", fontWeight: 700 }}
            >
              Target Type
            </Typography>
            <Chip
              size="small"
              label={data?.target_type || "-"}
              sx={{ mt: 0.5, borderRadius: 2 }}
            />
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: 12, color: "#94A3B8", fontWeight: 700 }}
            >
              Rollout
            </Typography>
            <Chip
              size="small"
              label={
                data?.rollout_strategy === "SCHEDULED"
                  ? "Scheduled"
                  : "Immediate"
              }
              color={
                data?.rollout_strategy === "SCHEDULED" ? "secondary" : "info"
              }
              sx={{ mt: 0.5, borderRadius: 2 }}
            />
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: 12, color: "#94A3B8", fontWeight: 700 }}
            >
              Deployment State
            </Typography>
            <Chip
              size="small"
              label={getProgressPhaseLabel(data)}
              color={
                data?.status === "FAILED"
                  ? "error"
                  : data?.status === "COMPLETED"
                    ? "success"
                    : data?.rollout_strategy === "SCHEDULED" &&
                        data?.status === "PENDING"
                      ? "secondary"
                      : data?.status === "IN_PROGRESS"
                        ? "info"
                        : "warning"
              }
              sx={{ mt: 0.5, borderRadius: 2 }}
            />
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: 12, color: "#94A3B8", fontWeight: 700 }}
            >
              Scheduled For
            </Typography>
            <Typography sx={{ fontWeight: 700, color: "#0F172A" }}>
              {data?.scheduled_at
                ? new Date(data.scheduled_at).toLocaleString()
                : "Immediate"}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: 12, color: "#94A3B8", fontWeight: 700 }}
            >
              Last Updated
            </Typography>
            <Typography sx={{ fontWeight: 700, color: "#0F172A" }}>
              {lastUpdated ? lastUpdated.toLocaleString() : "-"}
            </Typography>
          </Box>
        </Box>

        <Divider />
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            xl: "repeat(6, minmax(0, 1fr))",
          },
          gap: 2,
          mb: 2.5,
        }}
      >
        {summaryCards.map((card) => (
          <Box key={card.key}>
            <SummaryCard {...card} />
          </Box>
        ))}
      </Box>

      <DispatchOperationsPanel
        deploymentId={id}
        headers={headers}
        canManageDispatch={canManageDispatch}
      />

      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 4,
          border: "1px solid rgba(148,163,184,0.14)",
          boxShadow: "0 18px 40px rgba(15,23,42,0.06)",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 20, md: 22 }, mb: 1.5 }}>
          Failure Details
        </Typography>

        {!data && loading ? (
          <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : data?.failures?.length ? (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      color: "#64748B",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  <TableCell>Device</TableCell>
                  <TableCell>Error</TableCell>
                  <TableCell>Last Reported</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.failures.map((failure) => (
                  <TableRow key={failure.device_id} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {failure.device_id}
                    </TableCell>
                    <TableCell sx={{ minWidth: 240 }}>
                      {failure.error_message || "-"}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatTimestampAdd530(failure.last_reported_at) || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="success" sx={{ borderRadius: 3 }}>
            No failed devices reported for this deployment yet.
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
